/**
 * ProjectsPage — lists all projects the user can access.
 * Admin sees every project; members see only those they belong to.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Folder, Users } from 'lucide-react';
import { useTMAuth } from '@/contexts/TMAuthContext';
import { getAllProjects, getMyProjects } from '@/services/tmService';
import type { TMProject } from '@/types/tm.types';
import { PROJECT_COLORS, PROJECT_STATUS_LABELS } from '@/types/tm.types';
import { cn } from '@/utils/cn';

function colorFor(key: string) {
  return PROJECT_COLORS.find(c => c.key === key) ?? PROJECT_COLORS[0];
}

export default function ProjectsPage() {
  const { tmUser, isTMAdmin } = useTMAuth();
  const [projects, setProjects] = useState<TMProject[]>([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    if (!tmUser) return;
    const load = async () => {
      setLoading(true);
      const data = isTMAdmin
        ? await getAllProjects()
        : await getMyProjects(tmUser.uid);
      setProjects(data);
      setLoading(false);
    };
    load();
  }, [tmUser, isTMAdmin]);

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Projects</h1>
        {isTMAdmin && (
          <Link to="/tm/projects/new"
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold active:scale-95 transition-all">
            <Plus className="h-4 w-4" /> New
          </Link>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-28 rounded-2xl bg-gray-100 animate-pulse" />
          ))}
        </div>
      ) : projects.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
          <Folder className="h-10 w-10 text-gray-200 mx-auto mb-3" />
          <p className="text-sm text-gray-400 font-medium">No projects yet.</p>
          {isTMAdmin && (
            <Link to="/tm/projects/new"
              className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 text-white text-xs font-semibold">
              <Plus className="h-3.5 w-3.5" /> Create first project
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {projects.map(project => {
            const color = colorFor(project.color);
            return (
              <Link key={project.id} to={`/tm/projects/${project.id}`}
                className="block bg-white rounded-2xl border border-gray-100 shadow-sm p-4 active:bg-gray-50 transition-colors">
                <div className="flex items-start gap-3">
                  {/* Color dot / icon */}
                  <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center shrink-0', color.light)}>
                    <Folder className={cn('h-5 w-5', color.text)} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-bold text-gray-900 truncate">{project.name}</p>
                      <span className={cn(
                        'shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full',
                        project.status === 'active'    ? 'bg-emerald-50 text-emerald-600' :
                        project.status === 'completed' ? 'bg-blue-50 text-blue-600' :
                                                          'bg-gray-100 text-gray-400',
                      )}>
                        {PROJECT_STATUS_LABELS[project.status]}
                      </span>
                    </div>
                    {project.description && (
                      <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{project.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2">
                      <span className="flex items-center gap-1 text-[10px] text-gray-400">
                        <Users className="h-3 w-3" />
                        {project.memberUids.length} member{project.memberUids.length !== 1 ? 's' : ''}
                      </span>
                      {project.createdAt && (
                        <span className="text-[10px] text-gray-400">
                          {project.createdAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Color bar */}
                <div className={cn('mt-3 h-1 rounded-full w-full', color.bg, 'opacity-30')} />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
